import React, { useRef } from 'react';
import { number, object } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import Sticky from 'react-stickynode';
import useStyles from 'hooks/useStyles';
import cx from 'classnames';
import history from 'history.js';

import {
  getLiveTopic,
  getLiveTopicInfo,
  getLiveFilterInfo,
} from 'reduxModules/live/selector';
import { sendGTMEvent } from 'reduxModules/googleTagManager/action';
import { push } from 'reduxModules/navigation/action';
import useLiveMainCheckoutCollectionUrl from 'routes/live/hooks/useLiveMainCheckoutCollectionUrl';
import SnapaskHeader from 'components/SnapaskHeader';
import SnapaskFooter from 'components/SnapaskFooter';
import SnapaskLocaleSelection from 'components/SnapaskLocaleSelection';
import InstallAppForDesktopHandler from 'components/InstallAppForDesktopHandler';
import AlertBanner from 'components/AlertBanner';
import BreadcrumbSwitch from 'components/BreadcrumbSwitch';
import Tabs from 'components/Tabs';
import PaymentOptionList from 'components/PaymentOptionList';
import InstallAppForMobileHandler from 'components/InstallAppForMobileHandler';
import GetFreeTrialButton from 'components/GetFreeTrialButton';

import ContextStore from 'utils/createContext';
import useGetCurrentDevice from 'hooks/useGetCurrentDevice';
import useGetOfficialSiteURL from 'hooks/useGetOfficialSiteURL';

import { REGULAR_CLASS, SYLLABUS_LESSON } from 'constants/contentTypes';
import { LIVE_TOPIC_LESSON_TEMPLATE } from 'constants/routingPath';
import {
  REGULAR_CLASS_PURCHASE_ENTER,
  REGULAR_CLASS_SYLLABUS_LESSON_CLICK,
  REGULAR_CLASS_EPISODE_CLICK,
  REGULAR_CLASS_PURCHASE_CLICK,
} from 'constants/gtmEvent';

import {
  KEY_POINT,
  INSTRUCTOR,
  SUMMARY,
  QUESTIONS,
} from '../constants/topicTabs';

import LiveLayout from '../components/LiveLayout';
import useGetPathList from '../hooks/useGetPathList';
import PurchaseArea from './components/PurchaseArea';
import EpisodeList from '../components/EpisodeList';
import HtmlRenderer from './components/HtmlRenderer';
import Instructor from '../components/Instructor';
import Questions from '../components/Questions';

import styles from './LiveTopicSyllabus.scss';

const getGtmEventData = (topicId, topicName, lesson) => ({
  topicId,
  topicName,
  liveTime: lesson.startAt,
  episodeStatus: lesson.status,
  episodeId: lesson.id,
  episodeName: lesson.name,
  section: SYLLABUS_LESSON,
});

function LiveTopicSyllabus({ liveTopicId, syllabusLesson }) {
  useStyles(styles);
  const dispatch = useDispatch();
  const getOfficialSiteURL = useGetOfficialSiteURL();
  const { t } = useTranslation(['live']);
  const contentDomRef = useRef();
  const { location } = history;
  const singleTopicCheckoutCollectionUrl = useLiveMainCheckoutCollectionUrl();
  const { isMobile, isTablet } = useGetCurrentDevice();

  const {
    tabKeyList,
    freeLessonList,
    finishedLessons,
    scheduledLessons: { allLiveLessons },
    liveTopicInfo,
    syllabuses,
  } = useSelector(getLiveTopic);
  const {
    pictureUrl,
    name,
    isEnrolled,
    active: isActive,
    isPreOrdered,
    isSemester,
    semesterExpiredDays,
  } = useSelector(getLiveTopicInfo);
  const { selectedGradeLevel } = useSelector(getLiveFilterInfo);

  const pathList = useGetPathList({
    liveTopicInfo,
  });
  const bundleViewMode = location?.hash === '#view';
  const showPaymentOption =
    !isEnrolled && !isPreOrdered && isActive && !bundleViewMode;

  const tabComponentMapping = {
    [KEY_POINT]: {
      title: t('lesson.keypoint'),
      component: (
        <HtmlRenderer
          title={t('lesson.keypoint')}
          html={syllabusLesson.keyPointHtml}
          className={styles.keyPoint}
        />
      ),
      order: 0,
    },
    [SUMMARY]: {
      title: t('lesson.summary'),
      component: (
        <HtmlRenderer
          title={t('lesson.summary')}
          html={syllabusLesson.summaryHtml}
        />
      ),
      order: 1,
    },
    [INSTRUCTOR]: {
      title: t('topic.overview.detail.instructor'),
      component: <Instructor />,
      order: 2,
    },
    [QUESTIONS]: {
      title: t('topic.overview.detail.faq'),
      component: <Questions />,
      order: 3,
    },
  };

  if (!syllabusLesson.keyPointHtml) {
    delete tabComponentMapping[KEY_POINT];
  }

  if (!syllabusLesson.summaryHtml) {
    delete tabComponentMapping[SUMMARY];
  }

  const onClickPaymentOption = () => {
    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_PURCHASE_ENTER,
        data: {
          topicId: liveTopicId,
          topicName: name,
          gradeLevelName: selectedGradeLevel.name,
        },
      }),
    );
  };

  const onClicPurchaseButton = () => {
    dispatch(push(singleTopicCheckoutCollectionUrl));
  };

  const onSyllabusLessonClick = (lessonName, syllabusName) => {
    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_SYLLABUS_LESSON_CLICK,
        data: {
          syllabusLessonName: lessonName,
          topicName: name,
          syllabusName,
        },
      }),
    );
  };

  const renderTabs = tabKeyList.reduce((tabs, value) => {
    const { title, component, order } = tabComponentMapping[value] || {};
    if (!component) return tabs;

    // eslint-disable-next-line no-param-reassign
    tabs[order] = (
      <Tabs.TabPane
        tabKey={value}
        key={value}
        tabTitle={title}
        className={styles.tabs}
      >
        {component}
      </Tabs.TabPane>
    );

    return tabs;
  }, Array(Object.keys(tabComponentMapping).length));

  const onFreeLessonClick = lesson => {
    dispatch(
      push(
        getOfficialSiteURL(LIVE_TOPIC_LESSON_TEMPLATE, {
          topicId: liveTopicId,
          lessonId: lesson.id,
        }),
      ),
    );

    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_EPISODE_CLICK,
        data: getGtmEventData(liveTopicId, name, lesson),
      }),
    );
  };

  const onClickMobilePaymentOptionButton = () => {
    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_PURCHASE_CLICK,
        data: {
          topicId: liveTopicId,
          topicName: name,
        },
      }),
    );
  };

  return (
    <LiveLayout>
      <SnapaskHeader />
      <AlertBanner />
      <SnapaskLocaleSelection />
      <InstallAppForMobileHandler />
      <div className={styles.root}>
        <ContextStore.Provider
          value={{
            isTopicEnrolled: isEnrolled,
            viewMode: bundleViewMode,
            liveTopicId,
            liveTopicName: name,
            freeLessonList,
            finishedLessons,
            currentLesson: syllabusLesson,
            upcomingLessons: allLiveLessons,
            isSemester,
            semesterExpiredDays,
            syllabuses,
            onClickLesson: onFreeLessonClick,
          }}
        >
          <BreadcrumbSwitch pathList={pathList} className={styles.breadcrumb} />
          <h1 className={styles.title}>{syllabusLesson.name}</h1>
          <div className={styles.wrapper}>
            <PurchaseArea
              className={styles.player}
              imageUrl={pictureUrl}
              title={t('live:topic.videoPlayerStatus.unlock.title')}
              description={t('live:topic.videoPlayerStatus.unlock.description')}
              buttonText={t('live:topic.videoPlayerStatus.unlock.cta')}
              onButtonClick={onClicPurchaseButton}
              liveTopicId={liveTopicId}
              liveTopicName={name}
            />
            <div className={styles.sidebar}>
              <EpisodeList
                className={styles.episodeList}
                onSyllabusLessonClick={onSyllabusLessonClick}
              />
              <div className={styles.installApp}>
                <InstallAppForDesktopHandler />
              </div>
              <Sticky
                top={88}
                enabled={!isMobile}
                bottomBoundary={
                  contentDomRef.current?.getBoundingClientRect().bottom
                }
              >
                <div
                  className={cx(styles.paymentOption, {
                    [styles.showPaymentOption]: showPaymentOption,
                  })}
                >
                  <PaymentOptionList
                    topicId={liveTopicId}
                    topicName={name}
                    contentType={REGULAR_CLASS}
                    contentId={liveTopicId}
                    onClickPaymentOption={onClickPaymentOption}
                    onClickCtaButton={onClickMobilePaymentOptionButton}
                  />
                </div>
              </Sticky>
              <GetFreeTrialButton
                isShow={isMobile || isTablet}
                reversed
                block
                id={liveTopicId}
                name={name}
                type="regular_class"
                section="syllabus_paywall"
              />
            </div>
            <div className={styles.content} ref={contentDomRef}>
              <Tabs mode="anchor">{renderTabs}</Tabs>
            </div>
          </div>
        </ContextStore.Provider>
      </div>
      <SnapaskFooter />
    </LiveLayout>
  );
}

LiveTopicSyllabus.propTypes = {
  liveTopicId: number.isRequired,
  syllabusLesson: object.isRequired,
};

export default LiveTopicSyllabus;
